import type { IPhase } from '~/types/estimates';
import { isPrestationSummaryStep, isRentalSummaryStep } from '~/utils/etaiement-rentals';

export type EtaiementCostCategory = 'prestation' | 'transport' | 'etudes' | 'location' | 'autres';

export const ETAIEMENT_COST_CATEGORY_LABELS: Record<EtaiementCostCategory, string> = {
  prestation: 'Prestation',
  transport: 'Transport',
  etudes: 'Études',
  location: 'Location',
  autres: 'Autres',
};

export const ETAIEMENT_COST_CATEGORY_ORDER: EtaiementCostCategory[] = [
  'prestation',
  'transport',
  'etudes',
  'location',
  'autres',
];

const isTransportSummaryStep = (value: string | null | undefined): boolean => (
  value === 'transport.go' || value === 'transport.back'
);

const isStudySummaryStep = (value: string | null | undefined): boolean => (
  value === 'study'
);

export const classifySummaryStep = (value: string): EtaiementCostCategory => {
  if (isTransportSummaryStep(value)) return 'transport';
  if (isPrestationSummaryStep(value)) return 'prestation';
  if (isRentalSummaryStep(value)) return 'location';
  if (isStudySummaryStep(value)) return 'etudes';
  return 'autres';
};

const roundAmount = (value: number): number => Math.round(value * 100) / 100;

export type EtaiementCostBreakdown = {
  categories: Record<EtaiementCostCategory, number>;
  totalHT: number;
};

export const computeEtaiementCostBreakdown = (
  phases: IPhase[] | null | undefined,
): EtaiementCostBreakdown => {
  const categories: Record<EtaiementCostCategory, number> = {
    prestation: 0,
    transport: 0,
    etudes: 0,
    location: 0,
    autres: 0,
  };

  phases?.forEach((phase) => {
    phase?.summary?.steps?.forEach((step) => {
      const total = Number(step.total);
      if (!Number.isFinite(total) || total <= 0) return;
      const category = classifySummaryStep(step.value);
      categories[category] += total;
    });
  });

  Object.keys(categories).forEach((key) => {
    categories[key as EtaiementCostCategory] = roundAmount(
      categories[key as EtaiementCostCategory],
    );
  });

  const totalHT = roundAmount(
    ETAIEMENT_COST_CATEGORY_ORDER.reduce(
      (sum, category) => sum + categories[category],
      0,
    ),
  );

  return { categories, totalHT };
};
